import { parseBooleanForBackend } from '@Sage/ssm-utils/backendScalars';
import { numberIfFinite } from 'utils/numbers';

import { axiosB2 } from './axiosB2';

/**
 * Parse B2 JSON/string envelope from axios `data`; throws on parse failure, empty body, or non-success State.
 * Success State is `1` or `'1'`. If `State`/`state` is absent, the envelope is accepted.
 * @returns {object} parsed envelope
 */
function assertB2EnvelopeSuccess(data) {
    let parsed;
    try {
        parsed = typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
        throw new Error('Invalid B2 response: JSON parse error');
    }
    if (parsed == null) {
        throw new Error('Empty B2 response');
    }
    const state = parsed.State ?? parsed.state;
    if (state !== undefined && state !== null && state !== '1' && state !== 1) {
        const msg = parsed.Message || parsed.message || 'B2 error';
        throw new Error(typeof msg === 'string' ? msg : 'B2 error');
    }
    return parsed;
}

export function getProductFolders(data) {
    return axiosB2.post(`GetProductosFamilySFM`, data);
}

export function createProductFamily(data) {
    return axiosB2.post(`CreateProductoFamily`, data);
}

export function updateProductFamily(data) {
    return axiosB2.post(`UpdateProductoFamily`, data);
}

export function deleteProductFamily(data) {
    return axiosB2.post(`DeleteProductoFamily`, data);
}

export function deleteProduct(id) {
    return axiosB2.post(`DeleteEntity`, {
        entityId: id,
        entityType: 'producto',
    });
}

export function getRelatedProducts({ id, offset = 0, pageSize = 1000 }) {
    return axiosB2.post(`GetProductosRelacionadosSFM`, {
        Id: id,
        intFirstRecortPosition: offset,
        intMaxResults: pageSize,
    });
}

export function deleteRelation({ id }) {
    return axiosB2.post('DeleteEntity', {
        entityType: 'RELATION',
        entityId: id,
        xmlFields: "<fields><field name='table' value='PRODUCTO'/></fields>",
    });
}

export function createRelation({ idParent, idChild, units, relationType }) {
    let xmlFields = '';
    xmlFields += `<field name='table' value='PRODUCTO'/>`;
    xmlFields += `<field name='parent' value='${idParent}'/>`;
    xmlFields += `<field name='child' value='${idChild}'/>`;
    xmlFields += `<field name='relationType' value='${relationType}'/>`;
    xmlFields += `<field name='units' value='${units}'/>`;
    return axiosB2.post('CreateEntity', {
        entityType: 'RELATION',
        xmlFields: `<fields>${xmlFields}</fields>`,
    });
}

export function updateRelation({ id, idParent, idChild, units, relationType }) {
    let xmlFields = '';
    xmlFields += `<field name='table' value='PRODUCTO'/>`;
    xmlFields += `<field name='parent' value='${idParent}'/>`;
    if (idChild) xmlFields += `<field name='child' value='${idChild}'/>`;
    xmlFields += `<field name='relationType' value='${relationType}'/>`;
    xmlFields += `<field name='units' value='${units}'/>`;
    return axiosB2.post('UpdateEntity', {
        entityType: 'RELATION',
        entityId: id,
        xmlFields: `<fields>${xmlFields}</fields>`,
    });
}

export function setProductFamily({ productId, familyId }) {
    return axiosB2.post(`SetProductFamily`, {
        productId,
        familyId,
    });
}

/**
 * GetProductUoMRelations — spec: top-level `idProducto`.
 * Accepts `idProduct` as alias (web4 callers).
 * Resolves with `Result` from the B2 JSON envelope; throws on parse failure or non-success `State`.
 */
export function getProductUoMRelations(params) {
    const idProducto = params.idProducto ?? params.idProduct;
    return axiosB2
        .post('GetProductUoMRelations', { idProducto: numberIfFinite(idProducto) })
        .then(({ data }) => {
            const parsed = assertB2EnvelopeSuccess(data);
            if (parsed.Result !== undefined) {
                return parsed.Result;
            }
            if (parsed.result !== undefined) {
                return parsed.result;
            }
            throw new Error('B2 response missing Result');
        });
}

/**
 * AddProductUoMRelation — spec: `idProducto` + string `relation_object` with string-typed fields.
 */
export function addProductUoMRelation(payload) {
    const {
        idProduct,
        idProducto,
        idUnitOfMeasure,
        name,
        extId,
        factor,
        minStep,
        useSpecialPrice,
        specialPrice,
        isBase,
        isActive,
        intOrder,
    } = payload;

    const relation = {
        idUnitOfMeasure: String(idUnitOfMeasure),
        name: name == null ? '' : String(name),
        factor: String(factor),
        minStep: String(minStep),
        useSpecialPrice: parseBooleanForBackend(useSpecialPrice),
        specialPrice: useSpecialPrice ? String(specialPrice) : null,
        isBase: parseBooleanForBackend(isBase),
        isActive: parseBooleanForBackend(isActive),
        intOrder: intOrder != null && intOrder !== '' ? String(intOrder) : '1',
    };
    if (extId != null && String(extId).trim() !== '') {
        relation.extId = String(extId);
    }

    return axiosB2
        .post('AddProductUoMRelation', {
            idProducto: numberIfFinite(idProducto ?? idProduct),
            relation_object: JSON.stringify(relation),
        })
        .then((response) => {
            assertB2EnvelopeSuccess(response.data);
            return response;
        });
}

/**
 * UpdateProductUoMRelation — spec: `relation_object` (string JSON), same field set as add, plus `id`.
 * `factor` / `minStep` are omitted when the UI locks them (relation in use on SO lines) so B2 does not receive stale copies.
 */
export function updateProductUoMRelation(payload) {
    const {
        id,
        idUnitOfMeasure,
        name,
        extId,
        factor,
        minStep,
        useSpecialPrice,
        specialPrice,
        isBase,
        isActive,
        intOrder,
    } = payload;

    const relation = {
        id: String(id),
        idUnitOfMeasure: String(idUnitOfMeasure),
        name: name == null ? '' : String(name),
        useSpecialPrice: parseBooleanForBackend(useSpecialPrice),
        specialPrice: useSpecialPrice ? String(specialPrice) : null,
        isBase: parseBooleanForBackend(isBase),
        isActive: parseBooleanForBackend(isActive),
        intOrder: intOrder != null && intOrder !== '' ? String(intOrder) : '1',
    };
    if (factor !== undefined && factor !== null) {
        relation.factor = String(factor);
    }
    if (minStep !== undefined && minStep !== null) {
        relation.minStep = String(minStep);
    }
    if (extId != null && String(extId).trim() !== '') {
        relation.extId = String(extId);
    }

    return axiosB2
        .post('UpdateProductUoMRelation', {
            relation_object: JSON.stringify(relation),
        })
        .then((response) => {
            assertB2EnvelopeSuccess(response.data);
            return response;
        });
}

/**
 * CheckProductUoMRelationInUse — whether the relation is referenced by any active sales order line.
 * `Result: true` means in use (keep factor / minStep read-only); `false` means safe to edit.
 */
export function checkProductUoMRelationInUse(params) {
    const idRelation = params.idRelation ?? params.id;
    return axiosB2
        .post('CheckProductUoMRelationInUse', { idRelation: numberIfFinite(idRelation) })
        .then(({ data }) => {
            const parsed = assertB2EnvelopeSuccess(data);
            const r = parsed.Result ?? parsed.result;
            if (typeof r === 'boolean') return r;
            if (r === 'true' || r === '1' || r === 1) return true;
            if (r === 'false' || r === '0' || r === 0) return false;
            throw new Error('B2 response: missing boolean Result for CheckProductUoMRelationInUse');
        });
}

/**
 * DeleteProductUoMRelation — spec: top-level `idProducto` + `idRelation`.
 * Accepts `id` as alias for `idRelation`.
 */
export function deleteProductUoMRelation(params) {
    const idProducto = params.idProducto ?? params.idProduct;
    const idRelation = params.idRelation ?? params.id;
    return axiosB2
        .post('DeleteProductUoMRelation', {
            idProducto: numberIfFinite(idProducto),
            idRelation: numberIfFinite(idRelation),
        })
        .then((response) => {
            assertB2EnvelopeSuccess(response.data);
            return response;
        });
}
